import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { Calendar, ArrowLeft, ArrowRight, User, Clock, Share2, Phone, Copy, Check, Tag } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const readTime = (content) => {
  const words = (content || '').replace(/<[^>]*>/g, '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

const CATEGORY_COLORS = {
  'Investment Tips': 'bg-amber-500/10 text-amber-600',
  'Property Guide': 'bg-blue-500/10 text-blue-600',
  'Project Update': 'bg-emerald-500/10 text-emerald-600',
  'Market Update': 'bg-purple-500/10 text-purple-600',
  'Legal': 'bg-red-500/10 text-red-600',
  'News': 'bg-cyan-500/10 text-cyan-600',
  'General': 'bg-slate-500/10 text-slate-600',
};

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [settings, setSettings] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/public/blogs/${slug}`).then(r => setBlog(r.data)).catch(() => {});
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (blog?.category) {
      api.get('/public/blogs').then(r => {
        const others = (r.data || []).filter(b => b.slug !== slug && b.category === blog.category).slice(0, 3);
        if (others.length < 3) {
          const more = (r.data || []).filter(b => b.slug !== slug && b.category !== blog.category).slice(0, 3 - others.length);
          setRelated([...others, ...more]);
        } else {
          setRelated(others);
        }
      }).catch(() => {});
    }
  }, [blog, slug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(blog.title + ' - ' + window.location.href)}`, '_blank');
  };

  if (!blog) return (
    <WebsiteLayout>
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[#DAA520] border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-500 text-sm">Loading...</span>
        </div>
      </div>
    </WebsiteLayout>
  );

  const catStyle = CATEGORY_COLORS[blog.category] || CATEGORY_COLORS['General'];
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": blog.title,
    "description": blog.meta_description || blog.excerpt,
    "author": { "@type": "Organization", "name": "Ganesa Homestate Pvt. Ltd." },
    "publisher": { "@type": "Organization", "name": "Ganesa Homestate Pvt. Ltd." },
    "datePublished": blog.created_at,
    "keywords": blog.keywords,
    "mainEntityOfPage": { "@type": "WebPage", "@id": window.location.href }
  };

  const pageTitle = `${blog.meta_title || blog.title || 'Blog'} | Ganesa Homestate Blog`;
  const pageDescription = blog.meta_description || blog.excerpt || '';
  const schemaString = JSON.stringify(articleSchema);

  return (
    <WebsiteLayout>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {blog.keywords && <meta name="keywords" content={blog.keywords} />}
        <meta property="og:title" content={blog.meta_title || blog.title || 'Blog'} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={window.location.href} />
        <script type="application/ld+json">{schemaString}</script>
      </Helmet>

      {/* Header */}
      <section className="relative bg-[#0A0A0F] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#DAA520]/5 via-transparent to-[#0A0A0F]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-10 sm:pt-10 sm:pb-16">
          <Link to="/blog" className="inline-flex items-center gap-2 text-slate-400 text-xs sm:text-sm mb-6 hover:text-[#DAA520] transition-colors group" data-testid="back-to-blog">
            <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Saare Articles
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`px-3 py-1 ${catStyle} text-xs font-bold uppercase tracking-wider rounded-full`}>{blog.category}</span>
            <span className="text-slate-500 text-xs flex items-center gap-1"><Clock size={12} /> {readTime(blog.content)} min read</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-5" data-testid="blog-title">{blog.title}</h1>
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-400">
            <span className="flex items-center gap-1.5"><User size={13} className="text-[#DAA520]" /> {blog.author || 'Ganesa Homestate'}</span>
            <span className="flex items-center gap-1.5"><Calendar size={13} className="text-[#DAA520]" /> {new Date(blog.created_at).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>

          {/* Share buttons */}
          <div className="flex items-center gap-2 mt-5">
            <span className="text-slate-500 text-xs mr-1"><Share2 size={13} /></span>
            <button onClick={shareWhatsApp} data-testid="share-whatsapp"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366]/10 text-[#25D366] text-xs font-medium rounded-full hover:bg-[#25D366]/20 transition-colors border border-[#25D366]/20">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              WhatsApp
            </button>
            <button onClick={copyLink} data-testid="copy-link"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A25] text-slate-400 text-xs font-medium rounded-full hover:text-white transition-colors border border-[#1A1A25]">
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8 sm:py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <article className="prose prose-base sm:prose-lg max-w-none
            prose-headings:text-[#0A0A0F] prose-headings:font-bold
            prose-h2:text-xl prose-h2:sm:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:border-l-4 prose-h2:border-[#DAA520] prose-h2:pl-4
            prose-h3:text-lg prose-h3:sm:text-xl prose-h3:mt-6 prose-h3:mb-3
            prose-p:text-slate-600 prose-p:leading-relaxed prose-p:text-sm prose-p:sm:text-base
            prose-li:text-slate-600 prose-li:text-sm prose-li:sm:text-base
            prose-strong:text-[#0A0A0F]
            prose-a:text-[#DAA520] prose-a:no-underline hover:prose-a:underline
            prose-blockquote:border-l-[#DAA520] prose-blockquote:bg-[#DAA520]/5 prose-blockquote:rounded-r-xl prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:not-italic
            prose-ul:pl-5 prose-ol:pl-5
            prose-table:text-sm"
            dangerouslySetInnerHTML={{ __html: blog.content }}
            data-testid="blog-content" />

          {/* Keywords Tags */}
          {blog.keywords && (
            <div className="mt-8 pt-6 border-t border-slate-100">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag size={14} className="text-slate-400" />
                {blog.keywords.split(',').map((kw, i) => (
                  <span key={i} className="px-3 py-1 bg-slate-50 text-slate-500 text-xs rounded-full border border-slate-100">{kw.trim()}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#0A0A0F] via-[#142038] to-[#0A0A0F] py-10 sm:py-16" data-testid="blog-enquiry-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#DAA520]/10 rounded-full text-[#DAA520] text-xs font-semibold mb-4 border border-[#DAA520]/20">
            <Phone size={12} /> Free Consultation
          </div>
          <h3 className="text-xl sm:text-3xl font-bold text-white mb-3">Khatushyam Ji Me Plot Chahiye?</h3>
          <p className="text-slate-400 text-sm sm:text-base max-w-lg mx-auto mb-6">Shri Shyama Township me premium plots available hain. Aaj hi free consultation book karein!</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact" className="px-8 py-3.5 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold rounded-xl shadow-lg shadow-[#DAA520]/20 hover:shadow-xl hover:scale-105 transition-all text-sm">
              Enquiry Form
            </Link>
            <a href={`tel:${settings?.phone || ''}`} className="px-8 py-3.5 border-2 border-[#DAA520]/30 text-[#DAA520] font-bold rounded-xl hover:bg-[#DAA520]/10 transition-all text-sm flex items-center justify-center gap-2">
              <Phone size={16} /> Call Now
            </a>
            <a href={`https://wa.me/${(settings?.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
              className="px-8 py-3.5 border-2 border-[#25D366]/30 text-[#25D366] font-bold rounded-xl hover:bg-[#25D366]/10 transition-all text-sm flex items-center justify-center gap-2">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Related Blogs */}
      {related.length > 0 && (
        <section className="py-10 sm:py-16 bg-[#F8F9FB]" data-testid="related-blogs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="w-8 h-0.5 bg-[#DAA520]" />
              <h3 className="text-lg sm:text-xl font-bold text-[#0A0A0F]">Related Articles</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((b, i) => (
                <Link key={b.id} to={`/blog/${b.slug}`} className="group block" data-testid={`related-${i}`}>
                  <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-slate-100">
                    <div className="h-1.5 bg-gradient-to-r from-[#DAA520] via-[#FFD700] to-[#DAA520]" />
                    <div className="p-5">
                      <span className={`inline-block px-2.5 py-0.5 ${CATEGORY_COLORS[b.category] || 'bg-slate-100 text-slate-600'} text-[10px] font-bold uppercase rounded-full mb-2.5`}>
                        {b.category}
                      </span>
                      <h4 className="font-bold text-sm sm:text-base text-[#0A0A0F] group-hover:text-[#DAA520] transition-colors line-clamp-2 mb-2 leading-snug">{b.title}</h4>
                      <span className="text-[#DAA520] text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        Padhein <ArrowRight size={12} />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </WebsiteLayout>
  );
}
